import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AppContext";
import { useLead } from "@/context/LeadDialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Package, LogOut } from "lucide-react";
import ReferralCard from "@/components/ReferralCard";

export default function Account() {
  const { user, ready, logout } = useAuth();
  const { openLead } = useLead();
  const location = useLocation();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    // If user was passed via navigation state from AuthCallback, use it
    const stateUser = location.state?.user;
    if (ready && !user && !stateUser) { nav("/login"); return; }
    if (user || stateUser) {
      api.get("/leads/mine", { withCredentials: true })
        .then(({ data }) => setLeads(data))
        .catch(() => setLeads([]))
        .finally(() => setLoading(false));
    }
  }, [user, ready, location.state, nav]);

  const active = user;
  if (!active) return <div className="py-24 text-center text-asure-text2">Loading…</div>;

  const onLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-14">
      <div className="flex items-center justify-between gap-6 flex-wrap">
        <div className="flex items-center gap-4">
          {active.picture ? (
            <img src={active.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-asure-forest" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-asure-forest text-white flex items-center justify-center font-display text-2xl">{(active.name || "A")[0]}</div>
          )}
          <div>
            <div className="eyebrow">Welcome back</div>
            <h1 className="font-display text-4xl mt-1">{active.name}</h1>
            <div className="text-asure-text2 mt-1 text-sm flex items-center gap-2"><Mail size={12} />{active.email}</div>
          </div>
        </div>
        <div className="flex gap-3">
          {active.role === "admin" && <Link to="/admin" className="btn-ghost">Admin Dashboard</Link>}
          <button onClick={onLogout} className="btn-ghost inline-flex items-center gap-2" data-testid="logout-btn"><LogOut size={14} />Sign Out</button>
        </div>
      </div>

      <ReferralCard />

      <div className="mt-12">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="eyebrow">Your Enquiries</div>
            <h2 className="font-display text-3xl mt-1">Your ASURE journey so far</h2>
          </div>
          <button onClick={() => openLead()} className="btn-primary" data-testid="account-new-enquiry">New Enquiry</button>
        </div>

        {loading ? (
          <div className="card-surface p-10 text-center text-asure-text2">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="card-surface p-10 text-center">
            <Package size={40} className="mx-auto text-asure-forest" />
            <div className="font-display text-2xl mt-4">No enquiries yet</div>
            <p className="text-asure-text2 mt-2 text-sm">When you submit an enquiry, it will show up here.</p>
            <button onClick={() => openLead()} className="btn-primary mt-5">Start an Enquiry</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {leads.map((l) => (
              <div key={l.id} className="card-surface p-6" data-testid={`my-lead-${l.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="eyebrow">{new Date(l.created_at).toDateString()}</div>
                    <div className="font-display text-xl mt-2 leading-tight">{l.product_interest}</div>
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-asure-ivory text-asure-forest">{l.status}</div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-asure-text2">
                  <div className="flex items-center gap-1"><Phone size={11} />{l.mobile}</div>
                  <div className="flex items-center gap-1"><MapPin size={11} />{l.city}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
